import classNames from "classnames";
import {
  // Copy,
  DotsVertical,
  Download,
  Edit,
  Info,
  Trash,
} from "../../../../ui/Icon/Icon";
import IconButton from "../../../../ui/IconButton/IconButton";
import { useDisclosure } from "../../../../ui/util/useDisclosure";
import ChangeImageModal from "./ChangeImageModal";
import styles from "./ArticleImage.module.scss";
import HeroPreviewModal from "./HeroPreviewModal";
import {
  FragmentType,
  getFragmentData,
  graphql,
} from "../../../../__generated__/gql";
import { useQuery } from "urql";
import Dropdown from "../../../../ui/Dropdown/Dropdown";
import { ImageProvider } from "../../../../__generated__/gql/graphql";

export const ArticleImageImageFragment = graphql(/* GraphQL */ `
  fragment ArticleImage on Image {
    id
    url
    alt
    width
    height
    authorName
    authorUrl
    searchTerm
    provider
  }
`);

const ImageGenerationSettingsQuery = graphql(/* GraphQL */ `
  query imageGenerationSettingsArticleImage {
    imageGenerationSettings {
      customImageSize {
        width
        height
      }
    }
  }
`);

const ArticleImageQuery = graphql(/* GraphQL */ `
  query articleImage($id: String!) {
    image(id: $id) {
      ...ArticleImage
    }
  }
`);

function ArticleImage({
  id,
  data,
  setImage,
  onDelete,
  className,
}: {
  id?: string;
  data?: FragmentType<typeof ArticleImageImageFragment>;
  setImage?: (image: { id: string; alt: string }) => void;
  onDelete?: () => void;
  className?: string;
}) {
  const [heroPreviewModalOpened, heroPreviewModal] = useDisclosure();
  const [changeImageModalOpened, changeImageModal] = useDisclosure();

  const [articleImageQueryResult] = useQuery({
    query: ArticleImageQuery,
    variables: { id: id! },
    pause: !!data || !id,
  });
  const [imageGenerationSettingsQueryResult] = useQuery({
    query: ImageGenerationSettingsQuery,
  });

  const image = getFragmentData(
    ArticleImageImageFragment,
    data ?? articleImageQueryResult.data?.image,
  );

  const imageGenerationSettings =
    imageGenerationSettingsQueryResult.data?.imageGenerationSettings;

  return (
    <figure className={styles.container}>
      <>
        <div
          className={classNames(styles.imageWrapper, className)}
          style={{
            paddingTop: `calc(${
              imageGenerationSettings?.customImageSize?.height ?? 800
            } / ${imageGenerationSettings?.customImageSize?.width ?? 1200} * 100%)`,
          }}
        >
          {image && (
            <img
              alt={image.alt ?? ""}
              className={styles.image}
              src={image.url}
            />
          )}
        </div>
        <footer className={styles.footer}>
          <IconButton
            className={styles.infoButton}
            IconComponent={Edit}
            onClick={changeImageModal.open}
            aria-label="Change image"
          />
          {image && (
            <>
              <IconButton
                className={styles.infoButton}
                IconComponent={Info}
                onClick={heroPreviewModal.open}
                aria-label="Image info"
              />
              <HeroPreviewModal
                isOpen={heroPreviewModalOpened}
                onClose={heroPreviewModal.close}
                image={{
                  id: image.id,
                  src: image.url,
                  alt: image.alt,
                  height: image.height,
                  width: image.width,
                  provider: image.provider ?? ImageProvider.Unsplash,
                  author:
                    image.authorName && image.authorUrl
                      ? {
                          name: image.authorName,
                          href: image.authorUrl,
                        }
                      : undefined,
                }}
              />
              <Dropdown
                asChild
                items={[
                  {
                    items: [
                      {
                        label: "Download",
                        IconComponent: Download,
                        onClick: () => {
                          const link = document.createElement("a");
                          link.href = `${
                            import.meta.env.VITE_IMAGE_HOST
                          }/${id}?download=true`;
                          link.download = `${id}.jpg`;
                          link.target = "_blank";
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                        },
                      },
                      // {
                      //   label: "Copy",
                      //   IconComponent: Copy,
                      //   onClick: async () => {
                      //     try {
                      //       const result = await fetch(
                      //         `${import.meta.env.VITE_IMAGE_HOST}/${id}`,
                      //       );
                      //       const blob = await result.blob();

                      //       await navigator.clipboard.write([
                      //         new ClipboardItem({
                      //           "image/png": blob,
                      //         }),
                      //       ]);
                      //     } catch (error) {
                      //       console.error(error);
                      //     }
                      //   },
                      // },
                      ...(onDelete
                        ? [
                            {
                              label: "Delete",
                              IconComponent: Trash,
                              onClick: onDelete,
                            },
                          ]
                        : []),
                    ],
                  },
                ]}
              >
                <IconButton
                  className={styles.infoButton}
                  IconComponent={DotsVertical}
                  aria-label="More options"
                />
              </Dropdown>
            </>
          )}
          <ChangeImageModal
            id={image?.id}
            defaultProvider={image?.provider ?? undefined}
            defaultSearchTerm={image?.searchTerm ?? ""}
            isOpen={changeImageModalOpened}
            onClose={changeImageModal.close}
            onSubmit={setImage}
          />
        </footer>
      </>
    </figure>
  );
}

export default ArticleImage;
